import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';

const RegisterEmployer = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    facilityName: '',
    contactName: '',
    email: '',
    password: '',
    confirmPassword: '',
    facilityType: '',
    city: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { auth } = await import('../api');
      const response = await auth.registerEmployer(
        { email: formData.email, password: formData.password },
        {
          facility_name: formData.facilityName,
          contact_name: formData.contactName,
          facility_type: formData.facilityType,
          city: formData.city,
          phone: formData.phone
        }
      );
      
      // Store token and user info
      localStorage.setItem('authToken', response.access_token);
      localStorage.setItem('userType', response.user_type);
      localStorage.setItem('userName', response.user_name);
      
      toast({
        title: 'Registration Successful',
        description: 'Welcome to Luma Med Global! Redirecting to your dashboard...',
      });
      
      setTimeout(() => {
        navigate('/employer/dashboard');
      }, 1500);
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.detail || 'An error occurred during registration',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-gray-200">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-3xl font-bold text-gray-900">Create Employer Account</CardTitle>
              <p className="text-gray-600 mt-2">Start recruiting exceptional medical professionals today</p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="facilityName">Healthcare Facility Name</Label>
                  <Input
                    id="facilityName"
                    name="facilityName"
                    value={formData.facilityName}
                    onChange={handleChange}
                    placeholder="King Fahad Medical City"
                    required
                    className="mt-2"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contactName">Contact Person Name</Label>
                    <Input
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder="Ahmed Al-Rashid"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="hr@hospital.com"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="facilityType">Facility Type</Label>
                    <Select onValueChange={(value) => setFormData({...formData, facilityType: value})} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clinic">Clinic / Polyclinic</SelectItem>
                        <SelectItem value="small-hospital">Small Hospital (50-200 beds)</SelectItem>
                        <SelectItem value="medium-hospital">Medium Hospital (200-500 beds)</SelectItem>
                        <SelectItem value="large-hospital">Large Hospital (500+ beds)</SelectItem>
                        <SelectItem value="government">Government Hospital</SelectItem>
                        <SelectItem value="private">Private Hospital</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Select onValueChange={(value) => setFormData({...formData, city: value})} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="riyadh">Riyadh</SelectItem>
                        <SelectItem value="jeddah">Jeddah</SelectItem>
                        <SelectItem value="dammam">Dammam</SelectItem>
                        <SelectItem value="mecca">Mecca</SelectItem>
                        <SelectItem value="medina">Medina</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+966 XX XXX XXXX"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required
                    className="mt-2"
                  />
                </div>

                <Button type="submit" className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white py-6">
                  Create Employer Account
                </Button>

                <div className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#42b1c2] hover:text-[#3a9fb0] font-medium">
                    Sign in
                  </Link>
                </div>

                <p className="text-xs text-center text-gray-500">
                  By creating an account, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterEmployer;